import * as React from 'react';
import {
  Form, FormField, FormItem, FormControl, FormMessage,
  Button, SvgBackgroundGenerator,
  ImageCropper, ImageCropperTrigger, ImageCropperPreview,
  ColorSelector
} from '@virlewood/board-79';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useOnboardingStore } from '../../stores/useOnboardingStore';
import { motion } from 'framer-motion';

const customizeCoverSchema = z.object({
  brand_color: z.string().min(1, 'Seleziona un colore'),
  default_cover_image: z.string().optional(),
  cover_image: z.string().optional()
});

type CustomizeCoverFormData = z.infer<typeof customizeCoverSchema>;

const PRESET_COLORS = [
  '#b91c1c', '#c2410c', '#FFC600', '#4d7c0f',
  '#047857', '#0e7490', '#0369a1', '#4338ca', '#c026d3', '#db2777'
];

const CustomizeCover = () => {
  const { updateData, data, nextStep } = useOnboardingStore(state => state);

  // State to track the uploaded image File (for ImageCropper component)
  const [uploadedImageFile, setUploadedImageFile] = React.useState<File | null>(null);

  const form = useForm<CustomizeCoverFormData>({
    resolver: zodResolver(customizeCoverSchema),
    mode: 'onChange',
    defaultValues: {
      brand_color: data.brand_color ?? '#FFC600',
      default_cover_image: data.default_cover_image ?? '',
      cover_image: data.cover_image ?? ''
    }
  });

  // Watch only brand_color for the SVG generator
  const brand_color = form.watch('brand_color');

  // Handle SVG generation - memoized to prevent unnecessary re-renders
  const handleSvgChange = React.useCallback((_svgCode: string, imageDataUrl: string) => {
    form.setValue('default_cover_image', imageDataUrl);
  }, [form]);

  // Handle image upload - set as cover_image
  const handleImageChange = React.useCallback((file: File | null) => {
    if (file) {
      // Store the File for the ImageCropper
      setUploadedImageFile(file);

      // Convert File to data URL for cover_image
      const reader = new FileReader();
      reader.onloadend = () => {
        form.setValue('cover_image', reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      // User removed image
      setUploadedImageFile(null);
      form.setValue('cover_image', '');
    }
  }, [form]);

  const onSubmit = (formData: CustomizeCoverFormData) => {
    // Save form data to store on submit
    updateData({
      brand_color: formData.brand_color,
      default_cover_image: formData.default_cover_image,
      cover_image: formData.cover_image
    });

    // Proceed to next step
    nextStep();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="w-full max-w-2xl mx-auto"
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">

          {/* Image Cropper - shows default_cover_image or uploaded preview */}
          <FormField
            control={form.control}
            name="cover_image"
            render={() => (
              <FormItem>
                <FormControl>
                  <ImageCropper
                    value={uploadedImageFile}
                    onValueChange={handleImageChange}
                    stencilShape="rectangle"
                    aspectRatio={14 / 10}
                  >
                    <ImageCropperTrigger
                      placeholder="Carica un'immagine personalizzata o usa lo sfondo generato"
                      className="mx-auto max-w-lg"
                    >
                      {form.getValues('cover_image') ? (<ImageCropperPreview />) : (
                        <SvgBackgroundGenerator
                          color={brand_color}
                          className={'rounded'}
                          shape="next-is-free"
                          text={data.business_name}
                          width={1400}
                          height={1000}
                          onChange={handleSvgChange}
                        />)}
                      <Button type={'button'} className={'absolute bottom-10 right-10'} variant={'outline'} startIcon={'hugeicons:image-upload'}>Carica Immagine</Button>
                    </ImageCropperTrigger>
                  </ImageCropper>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Color Selector */}
          <FormField
            control={form.control}
            name="brand_color"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ColorSelector
                    clearable={false}
                    size={'xs'}
                    value={field.value}
                    onValueChange={field.onChange}
                    colors={PRESET_COLORS}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className={'bg-background sticky bottom-0 w-full p-4 mask-t-from-80% mt-20 flex justify-center gap-4'}>
            <Button
              type={'submit'}
              variant="primary"
              size="lg"
              className="min-w-sm"
            >
              Continua
            </Button>
          </div>
        </form>
      </Form>
    </motion.div>
  );
};

export default CustomizeCover;
